import { z } from 'zod';
import {
  ANIM_LIMITS,
  ANIM_PRESETS,
  DESIGN_SIZE,
  EASINGS,
  PARTICLE_PRESETS,
  SAFE_INSET,
  VIDEO_SLOTS,
  type Orientation,
  type PresentationContract,
} from './presentation-contract.js';

/**
 * Declarative, allow-listed skin manifest.
 *
 * Every object is .strict(): unknown keys are rejected, so there is no field
 * that could carry probabilities, payouts, bet values, rules, RNG, logical
 * grid size, wallet endpoints, tenant scope or executable code.
 */

export const SKIN_SCHEMA_VERSION = '1';
export const RENDERER_VERSION = '1.0.0';

/** Licensed (SIL OFL 1.1) fonts bundled with the web client via @fontsource. */
export const APPROVED_FONTS = [
  'Orbitron',
  'Rajdhani',
  'Exo 2',
  'Audiowide',
  'Chakra Petch',
  'Bungee',
  'Fredoka',
  'Righteous',
  'Cinzel',
  'Baloo 2',
  'Russo One',
  'Space Grotesk',
  'Inter',
] as const;

export const IMAGE_MIMES = ['image/png', 'image/webp', 'image/jpeg'] as const;
export const AUDIO_MIMES = ['audio/mpeg', 'audio/ogg', 'audio/wav'] as const;
export const VIDEO_MIMES = ['video/mp4', 'video/webm'] as const;

export const SKIN_LIMITS = {
  maxAssets: 200,
  maxAssetBytes: 6_000_000,
  maxTotalBytes: 40_000_000,
  warnTotalBytes: 15_000_000,
  maxImageDim: 4096,
  maxAudioMs: 180_000,
  maxVideoMs: 15_000,
  maxStripFrames: 64,
} as const;

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'colour must be #RRGGBB');
export const assetKeySchema = z.string().regex(/^[a-z0-9][a-z0-9._-]{0,63}$/, 'asset key: lowercase letters, digits, . _ -');
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);
const slug = z.string().regex(/^[a-z0-9][a-z0-9-]{1,62}$/);
const shortText = (max: number) =>
  z
    .string()
    .max(max)
    .refine((s) => !/[<>]/.test(s), 'angle brackets are not allowed');

export const assetRecordSchema = z
  .object({
    hash: sha256,
    mime: z.enum([...IMAGE_MIMES, ...AUDIO_MIMES, ...VIDEO_MIMES]),
    bytes: z.number().int().positive().max(SKIN_LIMITS.maxAssetBytes),
    width: z.number().int().min(1).max(SKIN_LIMITS.maxImageDim).optional(),
    height: z.number().int().min(1).max(SKIN_LIMITS.maxImageDim).optional(),
    durationMs: z.number().int().min(1).max(SKIN_LIMITS.maxAudioMs).optional(),
    /** Horizontal sprite strip: the image holds `count` equal-width frames. */
    frames: z
      .object({ count: z.number().int().min(1).max(SKIN_LIMITS.maxStripFrames), fps: z.number().int().min(1).max(60) })
      .strict()
      .optional(),
    name: shortText(120),
    source: shortText(200),
    licence: shortText(120),
  })
  .strict();
export type AssetRecord = z.infer<typeof assetRecordSchema>;

export const animBindingSchema = z
  .object({
    preset: z.enum(ANIM_PRESETS),
    durationMs: z.number().int().min(ANIM_LIMITS.minMs).max(ANIM_LIMITS.maxMs),
    easing: z.enum(EASINGS),
    intensity: z.number().min(0).max(ANIM_LIMITS.maxIntensity).default(1),
    particles: z.enum(PARTICLE_PRESETS).optional(),
    /** Optional sprite-strip asset to play during this state. */
    frames: assetKeySchema.optional(),
    /** Optional audio cue played with this animation. */
    sfx: z.string().regex(/^[a-z][a-zA-Z0-9.]{0,40}$/).optional(),
    /** Tint applied during the animation. */
    tint: hex.optional(),
  })
  .strict();
export type AnimBinding = z.infer<typeof animBindingSchema>;

const symbolStates = z
  .object({
    idle: animBindingSchema.optional(),
    land: animBindingSchema.optional(),
    win: animBindingSchema.optional(),
    removal: animBindingSchema.optional(),
    feature: animBindingSchema.optional(),
  })
  .strict();

export const symbolSkinSchema = z
  .object({
    texture: assetKeySchema,
    label: shortText(40).pipe(z.string().min(1)),
    scale: z.number().min(0.5).max(1.25).default(1),
    states: symbolStates,
    valueStyle: z
      .object({ color: hex, stroke: hex, fontScale: z.number().min(0.5).max(1.5) })
      .strict()
      .optional(),
  })
  .strict();
export type SymbolSkin = z.infer<typeof symbolSkinSchema>;

export const layerSchema = z
  .object({
    id: z.string().regex(/^[a-z0-9-]{1,32}$/),
    asset: assetKeySchema.optional(),
    color: hex.optional(),
    gradient: z.tuple([hex, hex]).optional(),
    fit: z.enum(['cover', 'contain', 'tile']).default('cover'),
    opacity: z.number().min(0).max(1).default(1),
    /** Slow ambient drift amount (0 = static). Disabled under reduced motion. */
    drift: z.number().min(0).max(1).default(0),
    particles: z.enum(PARTICLE_PRESETS).optional(),
    particleDensity: z.number().min(0).max(1).default(0.4),
    tint: hex.optional(),
  })
  .strict();
export type Layer = z.infer<typeof layerSchema>;

export const rectSchema = z
  .object({
    x: z.number().min(-400).max(2400),
    y: z.number().min(-400).max(2400),
    w: z.number().min(1).max(2400),
    h: z.number().min(1).max(2400),
    z: z.number().int().min(-10).max(100).default(0),
    visible: z.boolean().default(true),
  })
  .strict();
export type Rect = z.infer<typeof rectSchema>;

const audioCueSchema = z
  .object({
    asset: assetKeySchema,
    volume: z.number().min(0).max(1).default(0.8),
    group: z.enum(['music', 'sfx', 'ui']),
    loop: z.boolean().default(false),
    loopStartMs: z.number().int().min(0).max(SKIN_LIMITS.maxAudioMs).optional(),
    loopEndMs: z.number().int().min(0).max(SKIN_LIMITS.maxAudioMs).optional(),
  })
  .strict();

export const themeSchema = z
  .object({
    fontDisplay: z.enum(APPROVED_FONTS),
    fontBody: z.enum(APPROVED_FONTS),
    colors: z
      .object({
        primary: hex,
        accent: hex,
        text: hex,
        panelBg: hex,
        panelText: hex,
        win: hex,
        boardBg: hex,
        cellBg: hex,
      })
      .strict(),
    panelStyle: z.enum(['glass', 'solid', 'outline', 'plate']),
    panelRadius: z.number().int().min(0).max(40),
    boardOpacity: z.number().min(0).max(1),
    boardRadius: z.number().int().min(0).max(60),
    cellRadius: z.number().int().min(0).max(40),
    gridLines: z.boolean(),
    /** Ornate frame / plate images are drawn as nine-slices: corner size as a fraction of the image (0 = stretch). */
    frameSlice: z.number().min(0).max(0.45).optional(),
    /** Thickness of the frame's straight edges (fraction of the frame image); the renderer scales it to the board gap. */
    frameBand: z.number().min(0).max(0.45).optional(),
    /**
     * Portrait "cabinet" presentation (phone-first games): the portrait layout is used on every screen, centred, with
     * the scenery filling the sides; the host places its controls inside the cabinet's base (bottom `cabinetBase` of it).
     */
    cabinet: z.boolean().optional(),
    cabinetBase: z.number().min(0.08).max(0.4).optional(),
    /** Height of the cabinet's animated header band (fraction of the cabinet), where slots cabinet.idle/celebrate play. */
    cabinetHeader: z.number().min(0.05).max(0.6).optional(),
    plateSlice: z.number().min(0).max(0.45).optional(),
    /**
     * Pokie-style big controls: a larger spin button and large, lit decision buttons (e.g. the card gamble's TAKE WIN /
     * GAMBLE / RED / BLACK / suits) on every screen size. Presentation only.
     */
    bigControls: z.boolean().optional(),
    /**
     * Character clips (slots character.idle/run/win/lose): where the rest pose stands inside the clip frame, as fractions
     * of the frame (its alpha bounding box). The clip is placed so this box lands exactly on the still `character` art.
     */
    /**
     * Which gamble follows a win on engines that offer more than one (round option `gambleGame`, e.g. g27 v1.2.0):
     * `cards` (the card gamble, default) or `drive` (the long drive). The web player sets the option from it.
     */
    gambleGame: z.enum(['cards', 'drive']).optional(),
    /** Drive for Spins (round option `spinDrive`, g27 v1.3.0): a feature starts with a tee shot that sets its spins. */
    spinDrive: z.boolean().optional(),
    /**
     * The scene of the spins feature when it is not the golf drive: `kick` = a rugby league conversion into tiered stands
     * (owner 2026-10-24: "how high up into the crowd ... they kick the ball through the goal posts is the level of Spins").
     * Fractions of the stadium plate (slot drive.course): the ball on its tee, the goal posts, the centre height of each
     * stand tier from the lowest (one per band of the draw), the box where the kicker clip's frame sits (slot drive.swing),
     * and the clip's moments: ball placed and kicker ready, run-up starts, boot meets ball (seconds). `ball`: where the ball
     * sits inside the clip's frame (fractions of the clip box); the clip clears it at impactS and the player flies the
     * same ball (slot drive.ball) from there (tools/asset-gen/kick-clip.py).
     */
    driveScene: z
      .object({
        kind: z.literal('kick'),
        tee: z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) }).strict(),
        posts: z.object({ left: z.number().min(0).max(1), right: z.number().min(0).max(1), bar: z.number().min(0).max(1), top: z.number().min(0).max(1) }).strict(),
        tiers: z.array(z.number().min(0).max(1)).min(2).max(8),
        clip: z.object({ x: z.number().min(-1).max(2), y: z.number().min(-1).max(2), w: z.number().min(0.05).max(3), h: z.number().min(0.05).max(3) }).strict(),
        readyS: z.number().min(0).max(20),
        runS: z.number().min(0).max(20),
        impactS: z.number().min(0).max(20),
        ball: z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1), w: z.number().min(0.005).max(1), h: z.number().min(0.005).max(1) }).strict().optional(),
        /** the kick is the whole scene (slot drive.course.motion is the kicker's video in the stadium, the ball kicked off the
         * boot in it; no clip box): the game's ball comes down into the tier once the video's ball has left the top of the
         * frame at ballS, at x exitX (owner 2026-10-24: "their feet dont connect with the ball") */
        full: z.boolean().optional(),
        ballS: z.number().min(0).max(20).optional(),
        exitX: z.number().min(0).max(1).optional(),
        exitY: z.number().min(-0.5).max(1).optional(),
        /** full scene: the top this fraction of the scene is kept out of view (the scene enlarged from its bottom edge), so the
         * video's ball has gone out of sight at ballS; the video holds there and the game's ball comes down from exitX / exitY */
        crop: z.number().min(0).max(0.3).optional(),
        /** full scene: where the game's ball comes down (x) and how far either side of it the draws spread, when the kicker
         * stands between the posts and would hide the ball and the fan there (Golden Point, owner 2026-10-25) */
        land: z.object({ x: z.number().min(0).max(1), spread: z.number().min(0).max(0.3) }).strict().optional(),
        /** full scene with filmed outcomes (cricket, owner 2026-10-24): after ballS the scene cuts to the drawn tier's own clip
         * (slots drive.outcome.1-5: a fielder fielding 1 / 2 / 3, the ball into the boards for 4, a fan catching a 6); landS =
         * the moment the result shows in it. No drawn ball. */
        outcomes: z.array(z.object({ landS: z.number().min(0).max(20) }).strict()).min(2).max(8).optional(),
        /** filmed outcomes, the skill meter's miss (v1.5.0, owner 2026-10-27: a bowled clip): the scene cuts to the miss clip
         * (slot drive.outcome.miss) at cutS, before the bat meets the ball, and the miss shows at landS in it */
        miss: z.object({ cutS: z.number().min(0).max(20), landS: z.number().min(0).max(20) }).strict().optional(),
        /** the tiers' names for the readout and the cheer (cricket: '1 RUN', '2 RUNS', '3 RUNS', 'FOUR!', 'SIX!') */
        tierNames: z.array(z.string().min(1).max(16)).min(2).max(8).optional(),
        /** where the win text sits on the skin's banner crest (fraction of the crest from the top) and its colour */
        plaque: z.object({ top: z.number().min(0).max(0.9), color: z.string().regex(/^#[0-9a-fA-F]{6}$/) }).strict().optional(),
      })
      .strict()
      .optional(),
    /** Long drive: where the golfer's rest pose stands inside the swing clip (slots drive.swing / drive.rest), as fractions. */
    driveSwingFit: z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1), w: z.number().min(0.05).max(1), h: z.number().min(0.05).max(1) }).strict().optional(),
    characterClipFit: z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1), w: z.number().min(0.05).max(1), h: z.number().min(0.05).max(1) }).strict().optional(),
  })
  .strict();
export type SkinTheme = z.infer<typeof themeSchema>;

export const skinManifestSchema = z
  .object({
    schemaVersion: z.literal(SKIN_SCHEMA_VERSION),
    skinId: slug,
    gameId: z.string().regex(/^g\d{2}-[a-z0-9-]+$/),
    contractId: z.string().max(80),
    renderer: z.string().regex(/^1\.\d+\.\d+$/),
    display: z
      .object({
        title: shortText(40).pipe(z.string().min(1)),
        tagline: shortText(90),
        labels: z.record(z.string().regex(/^[a-z][a-zA-Z0-9.-]{0,48}$/), shortText(60)),
      })
      .strict(),
    theme: themeSchema,
    assets: z.record(assetKeySchema, assetRecordSchema),
    /** Asset slot id -> asset key (e.g. "lobby.card" -> "img-lobby"). */
    slots: z.record(z.string().regex(/^[a-z][a-z0-9.]{0,40}$/), assetKeySchema),
    symbols: z.record(z.string().regex(/^[A-Z0-9_]{1,12}$/), symbolSkinSchema),
    backgrounds: z
      .object({ base: z.array(layerSchema).min(1).max(6), feature: z.array(layerSchema).max(6) })
      .strict(),
    layout: z
      .object({
        landscape: z.record(z.string().max(48), rectSchema),
        portrait: z.record(z.string().max(48), rectSchema),
      })
      .strict(),
    hooks: z.record(z.string().regex(/^[a-zA-Z][a-zA-Z0-9.]{0,40}$/), animBindingSchema),
    audio: z
      .object({
        masterVolume: z.number().min(0).max(1).default(0.8),
        cues: z.record(z.string().regex(/^[a-z][a-zA-Z0-9.]{0,40}$/), audioCueSchema),
      })
      .strict(),
    accessibility: z
      .object({
        highContrast: z.boolean(),
        valueOutline: z.boolean(),
        reducedMotion: z.enum(['static', 'fade']),
      })
      .strict(),
    provenance: z.object({ createdWith: shortText(120), notes: shortText(600) }).strict(),
  })
  .strict();

export type SkinManifest = z.infer<typeof skinManifestSchema>;
export type SkinManifestInput = z.input<typeof skinManifestSchema>;

export interface SkinIssue {
  severity: 'error' | 'warning';
  path: string;
  code: string;
  message: string;
}

export interface SkinValidationResult {
  ok: boolean;
  errors: SkinIssue[];
  warnings: SkinIssue[];
  manifest?: SkinManifest;
  totalBytes: number;
}

/** WCAG relative-luminance contrast ratio between two #RRGGBB colours. */
export function contrastRatio(a: string, b: string): number {
  const lum = (h: string) => {
    const c = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255).map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  };
  const [x, y] = [lum(a), lum(b)].sort((p, q) => q - p);
  return (x + 0.05) / (y + 0.05);
}

/**
 * Validate a manifest against the schema AND its game's presentation contract.
 * Used by the editor (live), the publish job, the launch route and CI.
 */
export function validateSkin(input: unknown, contract: PresentationContract): SkinValidationResult {
  const errors: SkinIssue[] = [];
  const warnings: SkinIssue[] = [];
  const err = (path: string, code: string, message: string): void => {
    errors.push({ severity: 'error', path, code, message });
  };
  const warn = (path: string, code: string, message: string): void => {
    warnings.push({ severity: 'warning', path, code, message });
  };

  const parsed = skinManifestSchema.safeParse(input);
  if (!parsed.success) {
    for (const i of parsed.error.issues) err(i.path.join('.'), `schema.${i.code}`, i.message);
    return { ok: false, errors, warnings, totalBytes: 0 };
  }
  const m = parsed.data;

  if (m.gameId !== contract.gameId) err('gameId', 'contract.game', `skin is for ${m.gameId}, contract is ${contract.gameId}`);
  if (m.contractId !== contract.id) err('contractId', 'contract.id', `skin targets ${m.contractId}; game publishes ${contract.id}`);

  const kindOf = (mime: string): 'image' | 'audio' | 'video' =>
    (IMAGE_MIMES as readonly string[]).includes(mime) ? 'image' : (VIDEO_MIMES as readonly string[]).includes(mime) ? 'video' : 'audio';
  const asset = (key: string | undefined, path: string, kind: 'image' | 'audio' | 'video'): void => {
    if (!key) return;
    const a = m.assets[key];
    if (!a) return err(path, 'asset.missing', `asset "${key}" is not in the asset list`);
    if (kindOf(a.mime) !== kind) err(path, 'asset.kind', `"${key}" must be ${kind === 'image' ? 'an image' : kind}`);
  };

  // Assets
  const keys = Object.keys(m.assets);
  if (keys.length > SKIN_LIMITS.maxAssets) err('assets', 'assets.count', `too many assets (${keys.length} > ${SKIN_LIMITS.maxAssets})`);
  let totalBytes = 0;
  for (const [k, a] of Object.entries(m.assets)) {
    totalBytes += a.bytes;
    const kind = kindOf(a.mime);
    if (kind !== 'audio' && (!a.width || !a.height)) err(`assets.${k}`, 'asset.dims', `${kind} dimensions are required`);
    if (kind !== 'image' && !a.durationMs) err(`assets.${k}`, 'asset.duration', `${kind} duration is required`);
    if (kind === 'video' && a.durationMs && a.durationMs > SKIN_LIMITS.maxVideoMs) err(`assets.${k}`, 'asset.duration', `video loops must be at most ${SKIN_LIMITS.maxVideoMs / 1000} s`);
    if (a.frames && a.width && a.width % a.frames.count !== 0) err(`assets.${k}.frames`, 'asset.strip', 'strip width must divide evenly by frame count');
  }
  if (totalBytes > SKIN_LIMITS.maxTotalBytes) err('assets', 'assets.size', `total payload ${totalBytes} bytes exceeds ${SKIN_LIMITS.maxTotalBytes}`);
  else if (totalBytes > SKIN_LIMITS.warnTotalBytes) warn('assets', 'assets.size', `total payload ${(totalBytes / 1e6).toFixed(1)} MB is above the ${SKIN_LIMITS.warnTotalBytes / 1e6} MB mobile budget`);

  // Slots
  const allowedSlots = new Set([...contract.requiredAssetSlots, ...contract.optionalAssetSlots]);
  for (const s of contract.requiredAssetSlots) {
    if (!m.slots[s]) err(`slots.${s}`, 'slot.missing', `required asset slot "${s}" is empty`);
  }
  for (const [s, k] of Object.entries(m.slots)) {
    if (!allowedSlots.has(s)) err(`slots.${s}`, 'slot.unknown', `slot "${s}" is not part of ${contract.id}`);
    asset(k, `slots.${s}`, VIDEO_SLOTS.includes(s) ? 'video' : 'image');
  }

  // Symbols
  const contractSymbols = new Map(contract.symbols.map((s) => [s.id, s]));
  for (const s of contract.symbols) {
    const sk = m.symbols[s.id];
    if (!sk) {
      err(`symbols.${s.id}`, 'symbol.missing', `symbol ${s.id} (${s.role}) has no artwork mapping`);
      continue;
    }
    asset(sk.texture, `symbols.${s.id}.texture`, 'image');
    for (const st of s.requiredStates) {
      if (!sk.states[st]) err(`symbols.${s.id}.states.${st}`, 'symbol.state', `symbol ${s.id} is missing required "${st}" state`);
    }
    for (const [st, b] of Object.entries(sk.states)) {
      if (b?.frames) asset(b.frames, `symbols.${s.id}.states.${st}.frames`, 'image');
    }
  }
  for (const id of Object.keys(m.symbols)) {
    if (!contractSymbols.has(id)) err(`symbols.${id}`, 'symbol.unknown', `symbol ${id} does not exist in ${contract.id}`);
  }

  // Backgrounds
  for (const phase of ['base', 'feature'] as const) {
    m.backgrounds[phase].forEach((l, i) => asset(l.asset, `backgrounds.${phase}.${i}.asset`, 'image'));
  }

  // Layout
  const elements = new Map(contract.elements.map((e) => [e.id, e]));
  for (const o of ['landscape', 'portrait'] as Orientation[]) {
    const size = DESIGN_SIZE[o];
    const lay = m.layout[o];
    for (const e of contract.elements) {
      const r = lay[e.id];
      if (!r) {
        if (e.required) err(`layout.${o}.${e.id}`, 'layout.missing', `required element "${e.id}" has no ${o} position`);
        continue;
      }
      if (!r.visible && e.required) err(`layout.${o}.${e.id}`, 'layout.hidden', `required element "${e.id}" cannot be hidden`);
      if (r.w < e.min.w || r.h < e.min.h) err(`layout.${o}.${e.id}`, 'layout.min', `"${e.id}" is smaller than ${e.min.w}x${e.min.h}`);
      if (r.w > e.max.w || r.h > e.max.h) err(`layout.${o}.${e.id}`, 'layout.max', `"${e.id}" is larger than ${e.max.w}x${e.max.h}`);
      if (e.kind === 'board' || e.kind === 'panel') {
        const inside =
          r.x >= SAFE_INSET && r.y >= SAFE_INSET && r.x + r.w <= size.w - SAFE_INSET && r.y + r.h <= size.h - SAFE_INSET;
        if (!inside) err(`layout.${o}.${e.id}`, 'layout.safeArea', `"${e.id}" must stay inside the ${o} safe area`);
      }
      if (e.lockAspect) {
        const ratio = r.w / r.h;
        if (Math.abs(ratio - e.lockAspect) / e.lockAspect > 0.03)
          err(`layout.${o}.${e.id}`, 'layout.aspect', `"${e.id}" must keep aspect ${e.lockAspect.toFixed(3)} (is ${ratio.toFixed(3)})`);
      }
      if (e.assetSlot && r.visible && !m.slots[e.assetSlot] && e.kind !== 'decor' && e.required)
        err(`layout.${o}.${e.id}`, 'layout.asset', `"${e.id}" is visible but slot "${e.assetSlot}" is empty`);
    }
    for (const id of Object.keys(lay)) if (!elements.has(id)) err(`layout.${o}.${id}`, 'layout.unknown', `unknown element "${id}"`);
    // Panels must not overlap the board (essential information stays readable).
    const board = lay['board'];
    if (board) {
      for (const e of contract.elements.filter((x) => x.kind === 'panel')) {
        const r = lay[e.id];
        if (r && r.visible && overlaps(board, r)) err(`layout.${o}.${e.id}`, 'layout.overlap', `panel "${e.id}" overlaps the board`);
      }
    }
  }

  // Hooks
  for (const h of contract.animationHooks) if (!m.hooks[h.id]) err(`hooks.${h.id}`, 'hook.missing', `animation hook "${h.id}" is not bound`);
  for (const id of Object.keys(m.hooks)) {
    if (!contract.animationHooks.some((h) => h.id === id)) err(`hooks.${id}`, 'hook.unknown', `hook "${id}" is not in ${contract.id}`);
    const f = m.hooks[id].frames;
    if (f) asset(f, `hooks.${id}.frames`, 'image');
  }

  // Audio
  for (const c of contract.audioCues) {
    const cue = m.audio.cues[c.id];
    if (!cue) {
      if (c.required) err(`audio.cues.${c.id}`, 'audio.missing', `required audio cue "${c.id}" is not mapped`);
      continue;
    }
    asset(cue.asset, `audio.cues.${c.id}.asset`, 'audio');
    if (cue.loopStartMs !== undefined && cue.loopEndMs !== undefined && cue.loopEndMs <= cue.loopStartMs)
      err(`audio.cues.${c.id}`, 'audio.loop', 'loop end must be after loop start');
  }
  for (const id of Object.keys(m.audio.cues)) {
    if (!contract.audioCues.some((c) => c.id === id)) err(`audio.cues.${id}`, 'audio.unknown', `cue "${id}" is not in ${contract.id}`);
  }
  const cueIds = new Set(Object.keys(m.audio.cues));
  const checkSfx = (b: { sfx?: string } | undefined, path: string) => {
    if (b?.sfx && !cueIds.has(b.sfx)) err(path, 'audio.sfx', `sfx "${b.sfx}" is not a mapped cue`);
  };
  for (const [id, b] of Object.entries(m.hooks)) checkSfx(b, `hooks.${id}.sfx`);
  for (const [id, s] of Object.entries(m.symbols)) for (const [st, b] of Object.entries(s.states)) checkSfx(b, `symbols.${id}.states.${st}.sfx`);

  // Labels
  const allowedLabels = new Set(contract.labelKeys);
  for (const k of Object.keys(m.display.labels)) if (!allowedLabels.has(k)) err(`display.labels.${k}`, 'label.unknown', `label "${k}" is not defined by ${contract.id}`);

  // Accessibility
  const cr = contrastRatio(m.theme.colors.panelText, m.theme.colors.panelBg);
  if (cr < 4.5) err('theme.colors.panelText', 'a11y.contrast', `panel text contrast ${cr.toFixed(2)}:1 is below 4.5:1`);
  const cr2 = contrastRatio(m.theme.colors.text, m.theme.colors.boardBg);
  if (cr2 < 3) warn('theme.colors.text', 'a11y.contrast', `text/board contrast ${cr2.toFixed(2)}:1 is below 3:1`);

  // Unused assets
  const used = new Set<string>();
  Object.values(m.slots).forEach((k) => used.add(k));
  Object.values(m.symbols).forEach((s) => {
    used.add(s.texture);
    Object.values(s.states).forEach((b) => b?.frames && used.add(b.frames));
  });
  [...m.backgrounds.base, ...m.backgrounds.feature].forEach((l) => l.asset && used.add(l.asset));
  Object.values(m.hooks).forEach((b) => b.frames && used.add(b.frames));
  Object.values(m.audio.cues).forEach((c) => used.add(c.asset));
  for (const k of keys) if (!used.has(k)) warn(`assets.${k}`, 'asset.unused', `asset "${k}" is not referenced`);

  return { ok: errors.length === 0, errors, warnings, manifest: m, totalBytes };
}

function overlaps(a: { x: number; y: number; w: number; h: number }, b: { x: number; y: number; w: number; h: number }): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}
